// Handle Google OAuth callback: read token from URL and authenticate

import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { userContext } from "../../ContextProvider";
import { useNotifications } from '../../components/appointments/notifications';
import DialogCreateUser from '../../components/appointments/createUser';
import DialogForgotPassword from '../../components/appointments/forgotPassword';

const brandName = process.env.REACT_APP_BRAND_NAME || 'Botamation';

const Login = () => {
  const { showSuccess, showError, NotificationComponent } = useNotifications();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateUserDialogOpen, setIsCreateUserDialogOpen] = useState(false);
  const [isForgotPasswordDialogOpen, setIsForgotPasswordDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useContext(userContext);

  useEffect(() => {
    setEmail("");
    setPassword("");

    // Show Google login error if present in URL
    const urlParams = new URLSearchParams(window.location.search);
    console.log("urlParams:", urlParams);
    console.log("All query params:", [...urlParams.entries()]);
    const authStatus = urlParams.get('auth_status');
    const errorMessage = urlParams.get('message');
    if (authStatus === 'error' && errorMessage) {
      showError(decodeURIComponent(errorMessage));
      // Optionally, clean up the URL so the error doesn't persist

      const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [location.search]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    // If Google login callback, redirect to middleware page with all params
    if (token) {
      // Use window.location.search to preserve all query params
      navigate(`/google-middleware${window.location.search}`);
    }
  }, [location.search, navigate]);

  const handleLogin = async (e) => {
    // Ensure we prevent form submission default behavior
    if (e) {
      e.preventDefault();
    }

    // Validate input
    if (!email) {
      showError("Please enter your email address");
      return;
    }

    if (!password) {
      showError("Please enter your password");
      return;
    }

    setIsLoading(true);
    try {
      // Remove legacy/duplicate keys before login

      // Don't log the credentials in the console for security reasons
      const loginPayload = {
        email,
        password
      };

      const response = await axios.post("/api/user/login", loginPayload);
      if (response.data.success) {
        // Store only the required user/account details in localStorage
        // DO NOT store acctId in localStorage
        localStorage.setItem('userName', response.data.user.name || '');
        localStorage.setItem('userEmail', response.data.user.email || '');
        localStorage.setItem('profileImageUrl', response.data.user.profileImageUrl || '');
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('userId', response.data.user.userId || '');
        // Store all account numbers as a simple array in localStorage
        // Set first account number in localStorage and update URL for dashboard linkage
        if (Array.isArray(response.data.accounts) && response.data.accounts.length > 0) {
          const firstAccount = response.data.accounts[0];
          if (firstAccount && firstAccount.acctNo) {
            localStorage.setItem('acctNo', firstAccount.acctNo);
            // Update URL with account number for immediate dashboard linkage
            window.history.replaceState(null, '', `/appointments?acc=${firstAccount.acctNo}`);
          }
        }
        // Remove any legacy/duplicate keys again just in case
        login(response.data.token);
        // Display success message from backend or use default
        const successMessage = response.data.message || "Login successful";
        showSuccess(successMessage);
        setTimeout(() => {
          // Create a custom event to ensure all components know authentication has changed
          window.dispatchEvent(new Event('authChange'));
          // Redirect to appointments page
          if (Array.isArray(response.data.accounts) && response.data.accounts.length > 0 && response.data.accounts[0].acctNo) {
            navigate(`/appointments?acc=${response.data.accounts[0].acctNo}`);
          } else {
            navigate(`/appointments`);
          }
        }, 2000);
      } else if (response.data.message) {
        // If server returns a non-success response with message, display it
        showError(response.data.message);
      }
    } catch (error) {
      // Display backend error message if available
      const errorMessage = error.response?.data?.message || "Invalid credentials. Please try again.";
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = () => {
    setIsCreateUserDialogOpen(true);
  };

  const handleCloseCreateUserDialog = () => {
    setIsCreateUserDialogOpen(false);
  };

  const handleForgotPassword = () => {
    setIsForgotPasswordDialogOpen(true);
  };

  const handleCloseForgotPasswordDialog = () => {
    setIsForgotPasswordDialogOpen(false);
  };
  return (
    <>
      <NotificationComponent />

      <div className="min-h-screen flex items-center justify-center bg-gray-50  pb-8 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8 mt-4">
          <div className="flex flex-col items-center">
            <img
              src={`/${brandName}.svg`}
              className="mx-auto h-20 w-auto"
              alt={`${brandName} Logo`}
            />
            <h2 className="mt-6 text-center text-2xl font-bold tracking-tight text-gray-900">
              Sign in to your account
            </h2>
          </div>
          <div className="bg-white px-8 py-8 shadow sm:rounded-lg">
            <form onSubmit={handleLogin} className="space-y-4">
              {/* Email Address */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="Enter your email address"
                  className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                />
              </div>
              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-2 flex items-center text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className={`flex w-full justify-center items-center rounded-md bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm ${isLoading ? "opacity-70 cursor-not-allowed" : "hover:bg-gray-700"} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black h-11`}
              >
                {isLoading ? "Processing..." : "Login"}
              </button>
              {/* Create Account and Forgot Password links */}
              <div className="flex justify-between items-center mt-2">
                <div className="flex gap-2 text-xs text-gray-400 font-normal">
                  <a
                    href="#"
                    onClick={handleCreateUser}
                    className="hover:text-black"
                  >
                    Create User
                  </a>
                  <span>|</span>
                  <a
                    href="#"
                    onClick={handleForgotPassword}
                    className="hover:text-black"
                  >
                    Forgot password?
                  </a>
                </div>
              </div>
            </form>
            {/* Divider for Google Login */}
            <div className="flex items-center my-6">
              <div className="flex-grow border-t border-gray-300"></div>
              <span className="mx-4 text-gray-400">or</span>
              <div className="flex-grow border-t border-gray-300"></div>
            </div>
            {/* Google Login Button */}
            <div className="flex flex-col items-center w-full">
              <div className="w-full max-w-xs flex flex-col gap-3">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      // Call backend to get Google login URL with mode=signin
                      const res = await fetch('/api/user/google', {
                        method: 'POST',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ mode: 'signin' })
                      });
                      if (res.ok) {
                        const data = await res.json();
                        if (data && data.url) {
                          console.log("Redirecting to Google login URL:", data.url);
                          window.location.href = data.url;
                        } else {
                          window.location.href = 'http://localhost:8083/api/user/login/google/oAuth2/callback'; // fallback
                        }
                      } else {
                        window.location.href = 'http://localhost:8083/api/user/login/google/oAuth2/callback'; // fallback
                      }
                    } catch {
                      window.location.href = 'http://localhost:8083/api/user/login/google/oAuth2/callback'; // fallback
                    }
                  }}
                  className="flex items-center justify-center px-4 border border-black rounded-md shadow-sm bg-black hover:bg-gray-900 text-white font-semibold w-full h-11"
                  style={{ gap: '12px', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
                >
                  <span className="flex items-center" style={{ minWidth: 20, minHeight: 20 }}>
                    <svg width="20" height="20" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_17_40)">
                        <path d="M47.532 24.552c0-1.636-.146-3.2-.418-4.704H24.48v9.02h13.02c-.56 3.02-2.24 5.58-4.78 7.3v6.06h7.72c4.52-4.16 7.09-10.28 7.09-17.676z" fill="#4285F4" />
                        <path d="M24.48 48c6.48 0 11.92-2.14 15.89-5.82l-7.72-6.06c-2.14 1.44-4.88 2.3-8.17 2.3-6.28 0-11.6-4.24-13.5-9.96H2.67v6.24C6.62 43.98 14.88 48 24.48 48z" fill="#34A853" />
                        <path d="M10.98 28.46c-.5-1.44-.78-2.98-.78-4.46s.28-3.02.78-4.46v-6.24H2.67A23.97 23.97 0 000 24c0 3.98.96 7.76 2.67 11.18l8.31-6.72z" fill="#FBBC05" />
                        <path d="M24.48 9.54c3.53 0 6.68 1.22 9.17 3.62l6.87-6.87C36.39 2.14 30.96 0 24.48 0 14.88 0 6.62 4.02 2.67 12.82l8.31 6.24c1.9-5.72 7.22-9.96 13.5-9.96z" fill="#EA4335" />
                      </g>
                      <defs>
                        <clipPath id="clip0_17_40">
                          <rect width="48" height="48" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </span>
                  <span>Sign in with Google</span>
                </button>
              </div>
            </div>
            {/* Modal for Create User */}
            {isCreateUserDialogOpen && (
              <DialogCreateUser
                isOpen={isCreateUserDialogOpen}
                onClose={handleCloseCreateUserDialog}
              />
            )}
            {/* Modal for Forgot Password */}
            {isForgotPasswordDialogOpen && (
              <DialogForgotPassword
                isOpen={isForgotPasswordDialogOpen}
                onClose={handleCloseForgotPasswordDialog}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
